import React, {Suspense, useContext, useEffect, useState} from 'react';
import {Outlet, useLocation, useNavigate, useParams} from "react-router-dom";


import {SettingsContext} from "./SettingsContext";
import {ToursContext} from "./ToursContext";
import {MenuContext} from './MenuContext';
import {PointContext} from './PointContext';
import SettingsService from "./services/SettingsService";
import i18n from "../i18n";
import {matchHomepagePath, matchPointPagePath} from "./utils/LanguageUtils";
import Header from "./components/Header";
import Menu from "./components/Menu";
import Modal from "./components/Modal";
import {useTranslation} from "react-i18next";
import Loading from "@/components/Loading";
import Overlay from "@/components/Overlay";
import TourService from "@/services/TourService";

const Layout =({children}) => {
    const params = useParams();
    const { language } = params;

    const [loading, setLoading] = useState(true);

    const { t } = useTranslation();
    const location = useLocation();

    const { tourSettings, setTourSettings } = useContext(SettingsContext);
    const { accountTours, setAccountTours, activeTour, setActiveTour } = useContext(ToursContext);

    const navigate = useNavigate();

    const pointContext = useContext(PointContext);

    const isHomePage = matchHomepagePath(location.pathname);
    const isPointPage = matchPointPagePath(location.pathname);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const menuContext = useContext(MenuContext)
    const { isMenuOpen, setIsMenuOpen } = menuContext;

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (!isMenuOpen) {
            document.documentElement.classList.add("overflow-hidden");
            document.body.classList.add("overflow-hidden");
        } else {
            document.documentElement.classList.remove("overflow-hidden");
            document.body.classList.remove("overflow-hidden");
        }
    }

    console.log(params, location, tourSettings);

    // Load the background image
    const [backgroundImage, setBackgroundImage] = useState(null);
    React.useEffect(() => {

        if (tourSettings?.main_picture) {
            setBackgroundImage(tourSettings?.main_picture.preview_url);
        }
    }, [tourSettings])

    useEffect(() => {

        console.log("check tour settings....", params, (tourSettings), activeTour);
        const fetchSettings = async () => {
            try {

                const data = await SettingsService.fetchTourSettings(i18n, params, activeTour);
                if (data) {

                    console.log("fetched data", data);

                    setTourSettings(data);

                    //set the default point navigation based on the tour settings
                    if (data.navigation) {
                        if (data.navigation.google_maps?.active) {
                            pointContext.switchPointNavigation("google_maps");
                        } else if (data.navigation.open_streetmap?.active) {
                            pointContext.switchPointNavigation("open_streetmap");
                        } else if (data.navigation.map?.active) {
                            pointContext.switchPointNavigation("map");
                        } else if (data.navigation.list?.active) {
                            pointContext.switchPointNavigation("list");
                        } else if (data.navigation.qr_code?.active) {
                            pointContext.switchPointNavigation("qr_code");
                        }
                        
                        // Special case for QR code on specific hostnames
                        if (data.navigation.qr_code?.active && (window.location.hostname === "mas.tourio.com" || window.location.hostname === "mas.dev.tourio.com")) {
                            pointContext.switchPointNavigation("qr_code");
                        }
                    }

                    // enable matomo visitor tracking
                    if (data.analytics_site_id) {
                        console.info("loading analytics site ID");
                        var _paq = window._paq = window._paq || [];
                        var u = process.env.MATOMO_API_URL;
                        _paq.push(['enableLinkTracking']);
                        (function () {
                            _paq.push(['setTrackerUrl', u + 'matomo.php']);
                            _paq.push(['setSiteId', data.analytics_site_id]);
                            var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
                            g.type = 'text/javascript';
                            g.async = true;
                            g.src = u + 'matomo.js';
                            s.parentNode.insertBefore(g, s);
                        })();
                    } else {
                        console.error("analytics site ID not configured");
                    }

                    // if settings are from a tour, set active tourId
                    if (data.id) {
                        setActiveTour(data.id)
                    }
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    navigate(`/${params.language ? `${params.language}/` : ''}not-found`);
                } else {
                    console.error(error);
                }
            }
        };

          if (!tourSettings) {
              console.log("no tourSettings defined!");
                fetchSettings();
            } else {
                setLoading(false);
            }


    }, [i18n.language, tourSettings, activeTour]);

    useEffect( () => {

        const fetchTours = async () => {
            console.log("async fetchTours");
            try {
                const tourResponse = await TourService.fetchTourList(i18n, params);
                if (tourResponse) {
                    console.log(tourResponse);

                    setAccountTours(tourResponse);

                    if (tourResponse.length === 1) {
                        console.log("set active Tour", tourResponse[0]);
                        setActiveTour(tourResponse[0].id);
                    }

                    setLoading(false);
                } else {
                    console.log("tours not found, redirect to not-found");
                    navigate(`/${params.language ? `${params.language}/` : ''}not-found`);
                }
            } catch (e ) {
                console.error(e);
            }
        };


        if (tourSettings && loading && !isPointPage && !activeTour) {
            fetchTours();
        }

    }, [i18n.language, accountTours, tourSettings, activeTour]);


    return(
        <div className={`h-full bg-gray-100`} style={{ backgroundColor: (!loading && isHomePage && tourSettings?.start_background_color) ? tourSettings.start_background_color : '', minHeight: 'calc(var(--vh) * 100)' }}>
            {loading && <Loading loading={loading}/>}

            {!loading && tourSettings &&
                <>

                {isMenuOpen && <Menu toggleMenu={toggleMenu}/>}

                <div className={`transition-opacity duration-500 ease-in-out ${loading ? 'opacity-0' : 'opacity-100'}`}>
                    {isHomePage && (
                        <div className="absolute top-0 z-10 w-full">
                            {/* Background image and gradient overlay */}
                            <div
                                style={{
                                    backgroundImage: `url(${backgroundImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center top',
                                    height: tourSettings.start_background_color ? '50vh' : '100vh',
                                }}
                                className="w-full"
                            >
                                { tourSettings.start_background_color &&
                                    <div
                                        className="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none"
                                        style={{
                                            background: `linear-gradient(to bottom, transparent, ${tourSettings.start_background_color})`
                                        }}
                                    /> }
                            </div>
                        </div>
                    )}

                    {/* Wrapper to ensure background color continues as content grows */}
                    <div className="relative w-full z-20">
                        {/* Main content container */}
                        <main className="w-full" style={{ minHeight: 'calc(var(--vh) * 100)' }}>
                            <div className="flex flex-col h-full" style={{ minHeight: 'calc(var(--vh) * 100)' }}>

                                {!isHomePage && (
                                    <Header
                                        isMenuOpen={isMenuOpen}
                                        toggleMenu={toggleMenu}
                                    />
                                )}

                                <Suspense fallback={<div>Loading page...</div>}>
                                    <Outlet context={[]} />
                                </Suspense>
                            </div>

                            {/* show pop-up point nearby or pop-up for more point info via maps */}
                            <Modal />

                            {/* show overlay for info and summary */}
                            <Overlay />


                        </main>
                    </div>
                </div>

                </>
            }
        </div>
    )
}

export default Layout;